import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ChevronLeft, Share2, CalendarDays, Video, Play, Eye, X, Users, Clock, ArrowRight, MessageSquare, Star, UserPlus, Check } from 'lucide-react';
import { fetchPublicCreatorPage, checkoutProduct, fetchProductReviews, isPortraitProduct, sendCreatorMessage, setFollowingCreator, type CreatorPage, type CreatorProduct, type ReviewSummary, type CreatorReaderListing, type StoreClip } from '@/services/creatorApi';
import { StoreClipsRow } from '@/components/StoreClipsRow';
import { fetchWallet } from '@/services/affiliateApi';
import { useToast } from '@/hooks/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { LoginModal } from '@/components/LoginModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatEventParts, formatEventLong } from '@/lib/eventTime';

// Lazy-loaded — only fetched when a visitor clicks a preview tile.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const STATIC_ROOT = '/api/s3/';

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// Dated class-feed products (e.g. ITA) carry an eventDate. Break it into the
// parts we surface: a big month/day badge on the tile + a full line under it.
// Viewer's own time zone, zone abbreviation included (see lib/eventTime).
const formatEventDate = (iso?: string) => formatEventParts(iso, 'short');

// The next time a product meets: its eventDate, or the soonest future entry of
// eventDates for a featured event that runs on several dates.
function nextOccurrenceMs(p: CreatorProduct, now: number): number | null {
  const times = [p.eventDate, ...(p.eventDates || [])]
    .map(iso => (iso ? new Date(iso).getTime() : NaN))
    .filter(t => isFinite(t) && t > now);
  return times.length ? Math.min(...times) : null;
}

// "2d 04h 12m 09s" until the start; days drop off inside the last day.
function countdownParts(ms: number): { label: string; value: string }[] {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    ...(d > 0 ? [{ label: d === 1 ? 'day' : 'days', value: String(d) }] : []),
    { label: 'hr', value: pad(h) },
    { label: 'min', value: pad(m) },
    { label: 'sec', value: pad(sec) },
  ];
}

// "With Zak Barnett Studios": a co-owned event names the other store(s) on it,
// linked, leaving out the store being viewed.
function CoOwnerLine({ product, storeSlug }: { product: CreatorProduct; storeSlug?: string }) {
  const others = (product.coOwners || []).filter(c => c.slug && c.slug !== storeSlug);
  if (!others.length) return null;
  return (
    <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
      With{' '}
      {others.map((c, i) => (
        <React.Fragment key={c.slug}>
          {i > 0 && (i === others.length - 1 ? ' and ' : ', ')}
          <Link to={`/creators/${c.slug}`} style={{ color: 'var(--hj-ink-2)', fontWeight: 600 }}>{c.displayName}</Link>
        </React.Fragment>
      ))}
    </div>
  );
}

const CreatorStorePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const { isAuthenticated } = useAuthContext();
  // The buyer's Hey Joe credit, so a card can say it applies before they tap Buy.
  const [walletCents, setWalletCents] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) { setWalletCents(0); return; }
    let alive = true;
    fetchWallet().then(w => { if (alive) setWalletCents(w.balanceCents || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [isAuthenticated]);
  const [page, setPage] = useState<CreatorPage | null>(null);
  const [products, setProducts] = useState<CreatorProduct[]>([]);
  const [pastProducts, setPastProducts] = useState<CreatorProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [previewProductId, setPreviewProductId] = useState<string | null>(null);
  const [reviewSummaries, setReviewSummaries] = useState<Record<string, ReviewSummary>>({});
  // One-on-one listing (the creator's reader profile) and the message-the-creator note.
  const [reader, setReader] = useState<CreatorReaderListing | null>(null);
  const [clips, setClips] = useState<StoreClip[]>([]);
  const [showMessage, setShowMessage] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  // Following: count, whether this viewer follows, and what to do after sign-in.
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [afterLogin, setAfterLogin] = useState<'message' | 'follow'>('message');
  // Ticks the "Next up" countdown.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Preserve ?preview=… when deep-linking to a course page so staged (unpublished)
  // ITA classes stay openable from the preview store.
  const previewParam = new URLSearchParams(window.location.search).get('preview');
  const courseHref = (id: string) => `/course/${id}${previewParam ? `?preview=${encodeURIComponent(previewParam)}` : ''}`;

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchPublicCreatorPage(slug)
      .then(({ page, products, pastProducts, reader, followerCount, isFollowing, clips }) => {
        setClips(clips || []);
        setPage(page);
        setProducts(products);
        setPastProducts(pastProducts || []);
        setReader(reader || null);
        setFollowerCount(followerCount || 0);
        setIsFollowing(!!isFollowing);
        // Fetch review summaries for all products
        Promise.all(
          products.map(p =>
            fetchProductReviews(p._id)
              .then(summary => ({ id: p._id, summary }))
              .catch(() => ({ id: p._id, summary: { reviews: [], averageRating: 0, totalReviews: 0 } }))
          )
        ).then(results => {
          const map: Record<string, ReviewSummary> = {};
          results.forEach(r => { map[r.id] = r.summary; });
          setReviewSummaries(map);
        });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    const name = page?.displayName || 'Creator';
    if (navigator.share) {
      try { await navigator.share({ title: `${name} — Hey Joe Plus`, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied to clipboard' });
    }
  };

  const handleShareProduct = async (productId: string, title: string) => {
    const url = `${window.location.origin}/course/${productId}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${title} — Hey Joe Plus`, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Course link copied to clipboard' });
    }
  };

  const handleBuy = async (productId: string) => {
    if (!isAuthenticated) {
      toast({ title: 'Sign in required', description: 'Please sign in to purchase.', variant: 'destructive' });
      return;
    }
    setCheckingOut(productId);
    try {
      // Hey Joe credit pays first, the card the rest, same as the product page's
      // default-on checkbox (Oct 11: the store's own buy button skipped it).
      const checkoutUrl = await checkoutProduct(productId, undefined, undefined, undefined, { useWallet: true });
      window.location.href = checkoutUrl;
    } catch (err: any) {
      toast({ title: 'Checkout failed', description: err.message, variant: 'destructive' });
      setCheckingOut(null);
    }
  };

  const openMessage = () => {
    if (!isAuthenticated) { setAfterLogin('message'); setShowLogin(true); return; }
    setShowMessage(true);
  };

  const toggleFollow = async (follow = !isFollowing) => {
    if (!slug || followBusy) return;
    if (!isAuthenticated) { setAfterLogin('follow'); setShowLogin(true); return; }
    setFollowBusy(true);
    try {
      const r = await setFollowingCreator(slug, follow);
      setIsFollowing(r.isFollowing);
      setFollowerCount(r.followerCount);
      if (r.isFollowing) toast({ title: `You follow ${page?.displayName || 'this creator'}`, description: "We'll email you when they post something new." });
    } catch (err: any) {
      toast({ title: 'Not saved', description: err.message, variant: 'destructive' });
    } finally {
      setFollowBusy(false);
    }
  };

  const handleSendNote = async () => {
    if (!slug || sending) return;
    const text = note.trim();
    if (text.length < 10) {
      toast({ title: 'Say a little more than that.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const { creatorFirst } = await sendCreatorMessage(slug, text);
      setShowMessage(false);
      setNote('');
      toast({ title: 'Sent', description: `${creatorFirst || page?.displayName} gets it by email and can reply straight to you.` });
    } catch (err: any) {
      toast({ title: 'Not sent', description: err.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--hj-bg)' }}
      >
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  if (error || !page) {
    return (
      <main
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
      >
        <div className="text-center">
          <h1
            className="font-hj-display m-0"
            style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em' }}
          >
            Creator not found.
          </h1>
          <p style={{ marginTop: 10, fontSize: 14, color: 'var(--hj-ink)' }}>
            This page may no longer be available.
          </p>
        </div>
      </main>
    );
  }

  const avatarUrl = page.imageUrl
    ? page.imageUrl.startsWith('http') || page.imageUrl.startsWith('/api/')
      ? page.imageUrl
      : `${STATIC_ROOT}${page.imageUrl}`
    : null;
  // Photos should FILL the avatar circle (cover). Only a logo/wordmark avatar
  // wants the contain-on-a-dark-disc treatment — applying that to a headshot
  // shrinks it onto a near-black circle (Jen & Francene read as "just black").
  const isLogoAvatar = /(?:logo|wordmark)\.(?:png|svg|webp|jpe?g)(?:$|\?|$)/i.test(page.imageUrl || '');

  // Hero stats — surface the store's live class activity so the header isn't bare.
  const nowMs = Date.now();
  const upcomingClasses = products.filter(p => p.eventDate && new Date(p.eventDate).getTime() > nowMs);
  const nextClassProduct = upcomingClasses
    .slice()
    .sort((a, b) => new Date(a.eventDate!).getTime() - new Date(b.eventDate!).getTime())[0];
  const nextEv = nextClassProduct ? formatEventDate(nextClassProduct.eventDate) : null;
  // "Next up": the soonest future occurrence across every product on the store.
  const nextUp = products
    .map(p => ({ p, t: nextOccurrenceMs(p, nowTick) }))
    .filter((x): x is { p: CreatorProduct; t: number } => x.t != null)
    .sort((a, b) => a.t - b.t)[0] || null;
  const nextUpHref = nextUp ? `${courseHref(nextUp.p._id)}${courseHref(nextUp.p._id).includes('?') ? '&' : '?'}reg=1` : '';
  const aboutText =
    page.bio?.trim() ||
    (upcomingClasses.length ? 'Live acting classes & workshops with working industry pros.' : '');

  const heroChip: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    background: 'var(--hj-beige-deep)',
    border: '1px solid var(--hj-beige-line)',
    color: 'var(--hj-beige-ink-2)',
    fontSize: 13,
    fontWeight: 600,
  };

  // The creator's reader profile, if they have one: the one-on-one listing. The
  // old isReader/readerId flag on the page is kept as a fallback link.
  const profile = reader?.freelancer_profile;
  const rates = [
    profile?.reader_rate != null ? { label: 'Reader', rate: profile.reader_rate } : null,
    profile?.reader_director_rate != null ? { label: 'Session director', rate: profile.reader_director_rate } : null,
    profile?.reader_coach_rate != null ? { label: 'Coaching', rate: profile.reader_coach_rate } : null,
  ].filter(Boolean) as { label: string; rate: number }[];
  const bookHref = reader ? `/reader/${reader._id}` : page.isReader && page.readerId ? `/reader/${page.readerId}` : null;
  // A studio store goes by its whole name ("Message The Acting Center"); a person by first name.
  const storeName = (page.displayName || '').trim();
  const isStudioName = /^the\s|\b(studios?|center|centre|productions|academy|school|workshops?|agency|company)\b/i.test(storeName);
  const firstName = (isStudioName ? storeName : (storeName || reader?.first_name || '').trim().split(/\s+/)[0]) || 'the creator';

  const heroDarkBtn: React.CSSProperties = {
    padding: '10px 18px',
    borderRadius: 999,
    background: 'oklch(0.18 0.005 70)',
    border: '1px solid oklch(0.18 0.005 70)',
    color: 'var(--hj-beige)',
    fontSize: 13.5,
    fontWeight: 600,
    fontFamily: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    minHeight: 44,
  };
  const heroOutlineBtn: React.CSSProperties = {
    ...heroDarkBtn,
    background: 'transparent',
    border: '1px solid var(--hj-beige-ink-3)',
    color: 'var(--hj-beige-ink)',
  };

  const ghostBtn: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 999,
    background: 'transparent',
    border: '1px solid var(--hj-hairline)',
    color: 'var(--hj-ink-2)',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textDecoration: 'none',
    minHeight: 36,
  };

  const primaryPill: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: 999,
    background: 'var(--hj-blue)',
    border: '1px solid var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textDecoration: 'none',
    minHeight: 36,
  };

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1120,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <Link to="/marketplace" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          Marketplace
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>{page.displayName}</span>
      </div>

      {/* Next up: the creator's next live event, counting down, one tap to register. */}
      {nextUp && (
        <section
          aria-label="Next up"
          className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
          style={{
            marginTop: 8,
            padding: '14px 16px',
            borderRadius: 'var(--hj-r-lg)',
            background: 'oklch(0.18 0.005 70)',
            color: 'var(--hj-beige)',
          }}
        >
          <div className="flex-1 min-w-0">
            <div className="mono-label" style={{ color: 'var(--hj-yellow)', marginBottom: 4 }}>Next up</div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {nextUp.p.title}
            </div>
            <div style={{ marginTop: 3, fontSize: 13, color: 'var(--hj-beige)' }}>
              {formatEventLong(new Date(nextUp.t).toISOString())}
            </div>
          </div>
          <div className="flex items-end gap-2.5" aria-live="off" role="timer">
            {countdownParts(nextUp.t - nowTick).map(c => (
              <div key={c.label} className="text-center" style={{ minWidth: 34 }}>
                <div className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{c.value}</div>
                <div className="mono-label" style={{ marginTop: 4, fontSize: 10, color: 'var(--hj-beige)' }}>{c.label}</div>
              </div>
            ))}
          </div>
          <Link
            to={nextUpHref}
            className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
            style={{ padding: '11px 20px', minHeight: 44, borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            Save my seat <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </section>
      )}

      {/* Hero — banner cover (if set) sits atop the profile info, as one card */}
      <section
        className="relative overflow-hidden"
        style={{
          marginTop: 8,
          borderRadius: 'var(--hj-r-lg)',
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        {(page as any).hidden && (
          <div style={{ padding: '10px 16px', background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
            This store is hidden from the public. Only you and Hey Joe admins can see this page.
          </div>
        )}
        {page.bannerImage && (
          <img
            src={page.bannerImage.startsWith('http') || page.bannerImage.startsWith('/api/') ? page.bannerImage : `${STATIC_ROOT}${page.bannerImage}`}
            alt={`${page.displayName} banner`}
            className="w-full block"
            style={{ aspectRatio: '16 / 5', objectFit: 'cover' }}
          />
        )}
        <button
          type="button"
          onClick={handleShare}
          className="absolute inline-flex items-center gap-1.5"
          style={{
            top: 18,
            right: 18,
            padding: '7px 12px',
            borderRadius: 999,
            background: 'transparent',
            border: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink-2)',
            fontSize: 12.5,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>

        {/* When a banner carries the visual weight, the info block below it is
            compact — roughly half the height (tighter padding + smaller avatar). */}
        <div style={{ padding: page.bannerImage ? 'clamp(14px, 2vw, 20px)' : 'clamp(24px, 4vw, 40px)' }}>
        <div className={`flex flex-col sm:flex-row items-center sm:items-start ${page.bannerImage ? 'gap-4' : 'gap-5'}`}>
          {avatarUrl ? (
            <div
              className="rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ width: page.bannerImage ? 80 : 112, height: page.bannerImage ? 80 : 112, background: 'oklch(0.18 0.005 70)', border: '3px solid var(--hj-blue)' }}
            >
              {/* Logo art (often a light/transparent wordmark) needs a dark disc to read;
                  contain + padding keeps the full mark visible instead of cropping it. */}
              <img
                src={avatarUrl}
                alt={page.displayName}
                className="w-full h-full"
                style={isLogoAvatar ? { objectFit: 'contain', padding: page.bannerImage ? 11 : 16 } : { objectFit: 'cover' }}
              />
            </div>
          ) : (
            <div
              className="rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                width: page.bannerImage ? 80 : 112,
                height: page.bannerImage ? 80 : 112,
                background: 'var(--hj-yellow-tint)',
                border: '3px solid var(--hj-blue)',
                fontSize: 36,
                fontWeight: 700,
                // --hj-blue is the brand yellow — unreadable on the beige hero.
                color: 'var(--hj-beige-ink)',
                fontFamily: 'var(--hj-font-mono)',
              }}
            >
              {page.displayName?.[0]}
            </div>
          )}
          <div className="text-center sm:text-left flex-1">
            <div className="mono-label" style={{ marginBottom: 6, color: 'var(--hj-beige-ink-3)' }}>
              Creator store
            </div>
            <h1
              className="font-hj-display m-0"
              style={{
                fontSize: 'clamp(32px, 4.6vw, 48px)',
                color: 'var(--hj-beige-ink)',
                letterSpacing: '-0.025em',
                fontWeight: 800,
                lineHeight: 1.02,
              }}
            >
              {page.displayName}
            </h1>
            {aboutText && (
              <p
                className="m-0"
                style={{
                  marginTop: 12,
                  fontSize: 15,
                  color: 'var(--hj-beige-ink-2)',
                  lineHeight: 1.55,
                  maxWidth: 560,
                }}
              >
                {aboutText}
              </p>
            )}
            {upcomingClasses.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start" style={{ marginTop: 16 }}>
                {upcomingClasses.length > 0 && (
                  <span style={heroChip}>
                    <CalendarDays className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-3)' }} />
                    {upcomingClasses.length} upcoming class{upcomingClasses.length === 1 ? '' : 'es'}
                  </span>
                )}
                {nextEv && (
                  <span style={heroChip}>
                    <Clock className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-3)' }} />
                    Next {nextEv.full}
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start" style={{ marginTop: 18 }}>
              {bookHref && (
                <Link to={bookHref} className="inline-flex items-center gap-2" style={heroDarkBtn}>
                  <CalendarDays className="w-4 h-4" /> Book a session
                </Link>
              )}
              <button type="button" onClick={openMessage} className="inline-flex items-center gap-2" style={bookHref ? heroOutlineBtn : heroDarkBtn}>
                <MessageSquare className="w-4 h-4" /> Message {firstName}
              </button>
              <button
                type="button"
                onClick={() => toggleFollow()}
                disabled={followBusy}
                aria-pressed={isFollowing}
                className="inline-flex items-center gap-2 disabled:cursor-not-allowed"
                style={{ ...heroOutlineBtn, ...(isFollowing ? { background: 'var(--hj-beige-deep)' } : {}), opacity: followBusy ? 0.6 : 1 }}
              >
                {isFollowing ? <Check className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {isFollowing ? 'Following' : 'Follow'}
                {followerCount > 0 && (
                  <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>{followerCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>
        </div>
      </section>

      <StoreClipsRow clips={clips} name={page.displayName || firstName} courseHref={courseHref} />

      {/* One-on-one: the creator's reader profile, surfaced as their coaching
          listing. Rates are per 15 minutes, same as the reader page. */}
      {reader && (rates.length > 0 || profile?.reader_experience) && (
        <section
          style={{
            marginTop: 20,
            padding: 'clamp(18px, 3vw, 28px)',
            borderRadius: 'var(--hj-r-lg)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            <div className="flex-1 min-w-0">
              <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>
                <Star size={11} strokeWidth={2} style={{ display: 'inline', marginRight: 6, color: 'var(--hj-yellow-ink)' }} />
                Work with {firstName} one-on-one
              </div>
              <h2 className="font-hj-display m-0" style={{ fontSize: 'clamp(22px, 3vw, 28px)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--hj-ink)', lineHeight: 1.1 }}>
                Private sessions over video
              </h2>
              {profile?.reader_experience && (
                <p className="m-0" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.55, color: 'var(--hj-ink-2)', maxWidth: 640, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {profile.reader_experience}
                </p>
              )}
              {!!profile?.reader_categories?.length && (
                <div className="flex flex-wrap gap-1.5" style={{ marginTop: 12 }}>
                  {profile.reader_categories.slice(0, 8).map(c => (
                    <span key={c} className="mono-label" style={{ padding: '4px 9px', borderRadius: 999, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 11 }}>{c}</span>
                  ))}
                </div>
              )}
              {profile?.reader_availability_notes && (
                <p className="m-0" style={{ marginTop: 12, fontSize: 13, color: 'var(--hj-ink-3)' }}>
                  <Clock size={12} style={{ display: 'inline', marginRight: 5, verticalAlign: '-2px' }} />
                  {profile.reader_availability_notes}{profile.timezone ? ` (${profile.timezone})` : ''}
                </p>
              )}
            </div>
            <div className="w-full sm:w-auto" style={{ minWidth: 220 }}>
              {rates.length > 0 && (
                <div style={{ borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-0, var(--hj-bg))', padding: '12px 14px' }}>
                  <div className="mono-label" style={{ color: 'var(--hj-ink-4)', marginBottom: 6 }}>Per 15 min</div>
                  {rates.map(r => (
                    <div key={r.label} className="flex items-baseline justify-between" style={{ padding: '5px 0', fontSize: 14, color: 'var(--hj-ink)' }}>
                      <span>{r.label}</span>
                      <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontWeight: 700 }}>{formatPrice(r.rate * 100)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-col gap-2" style={{ marginTop: 12 }}>
                {bookHref && (
                  <Link to={`${bookHref}?book=1`} className="inline-flex items-center justify-center gap-2" style={{ ...primaryPill, minHeight: 44, fontSize: 14 }}>
                    <CalendarDays className="w-4 h-4" /> Book a session
                  </Link>
                )}
                <button type="button" onClick={openMessage} className="inline-flex items-center justify-center gap-2" style={{ ...ghostBtn, minHeight: 44, fontSize: 14 }}>
                  <MessageSquare className="w-4 h-4" /> Ask {firstName} first
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Products */}
      <section style={{ marginTop: 28 }}>
        {products.length === 0 ? (
          <div
            className="text-center"
            style={{
              padding: '60px 24px',
              borderRadius: 'var(--hj-r-md)',
              border: '1.5px dashed var(--hj-hairline)',
              background: 'var(--hj-surface-1)',
              color: 'var(--hj-ink-3)',
              fontSize: 14,
            }}
          >
            No products available yet.
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-3" style={{ marginBottom: 18 }}>
              <h2
                className="font-hj-display m-0"
                style={{
                  fontSize: 22,
                  color: 'var(--hj-ink)',
                  letterSpacing: '-0.02em',
                  fontWeight: 700,
                }}
              >
                {products.length === 1 ? 'Available now' : 'Products'}
              </h2>
              <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                {products.length}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(product => {
                // Prefer the dedicated Mux preview clip when it's ready —
                // public playback id, no token needed.
                const muxPreviewReady =
                  product.muxPreview?.muxIngestStatus === 'ready' &&
                  !!product.muxPreview?.muxPlaybackId;
                const previewVideo = product.videos.find(v => v.freePreview && (v.url || (v.muxIngestStatus === 'ready' && v.muxPlaybackId)));
                // Dedicated preview clip first, else the free-preview lesson's own
                // ingested asset (public playback) — raw S3 <video> is the last resort.
                const previewMuxId = muxPreviewReady
                  ? product.muxPreview!.muxPlaybackId
                  : ((previewVideo?.muxIngestStatus === 'ready' && previewVideo?.muxPlaybackId) || null);
                const hasPreview = !!previewMuxId || !!previewVideo?.url;
                const isShowingPreview = previewProductId === product._id && hasPreview;
                const summary = reviewSummaries[product._id];
                // Dated class-feed products (ITA etc.) render as event cards: warm
                // date tile, date/instructor line, and a "View class" CTA → detail page.
                const ev = formatEventDate(product.eventDate);
                const isEvent = !!ev;
                // A finished featured event that has a recording is a REPLAY, not a seat. It
                // stays on the store deliberately, so say what it is — otherwise it
                // reads as an event you already missed.
                const isReplay = product.productType === 'webinar'
                  && !!product.eventDate && new Date(product.eventDate).getTime() < nowMs
                  && (product.videos || []).some((v: any) => v.muxPlaybackId || v.s3Key);
                const thumbUrl = product.coverImage
                  ? (product.coverImage.startsWith('http') || product.coverImage.startsWith('/api/') ? product.coverImage : `/api/s3/${product.coverImage}`)
                  : product.videos[0]?.thumbnailUrl
                    ? (product.videos[0].thumbnailUrl.startsWith('http') ? product.videos[0].thumbnailUrl : `/api/s3/${product.videos[0].thumbnailUrl}`)
                    : null;
                // Logo-fallback covers (e.g. ITA's ita-logo.png) render small + centered on the
                // dark media; real photos (headshots, class art) cover-fill like the reference.
                const isLogoImg = !!thumbUrl && /(?:ita-logo|logo)\.(?:png|svg|webp|jpe?g)(?:$|\?)/i.test(thumbUrl);
                const hasMemberPrice = typeof product.memberPriceCents === 'number' && product.memberPriceCents < product.price;
                const seatsFull = product.seatsRemaining === 0;
                // Vertical series (majority-portrait episodes) get a 2:3 poster crop.
                const vertical = product.format === 'series' && isPortraitProduct(product.videos);
                const epCount = product.videos.length;
                return (
                  <div
                    key={product._id}
                    className="overflow-hidden flex flex-col"
                    style={{
                      borderRadius: 'var(--hj-r-lg)',
                      background: 'var(--hj-beige)',
                      border: '1px solid var(--hj-beige-line)',
                    }}
                  >
                    <div
                      className="flex items-center justify-center relative overflow-hidden"
                      style={{ aspectRatio: vertical ? '2 / 3' : '16 / 9', background: 'oklch(0.16 0.008 60)' }}
                    >
                      {isShowingPreview ? (
                        <>
                          {previewMuxId ? (
                            <Suspense fallback={<div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'oklch(0.7 0 0)' }} /></div>}>
                              <MuxPlayer
                                key={`mux-preview-${previewMuxId}`}
                                playbackId={previewMuxId}
                                autoPlay
                                style={{ width: '100%', height: '100%' }}
                                metadata={{ video_title: `${product.title} preview` }}
                              />
                            </Suspense>
                          ) : (
                            <video src={previewVideo!.url} className="w-full h-full" controls autoPlay playsInline />
                          )}
                          <button
                            onClick={() => setPreviewProductId(null)}
                            aria-label="Close preview"
                            className="absolute"
                            style={{
                              top: 8,
                              right: 8,
                              width: 28,
                              height: 28,
                              borderRadius: 999,
                              background: 'oklch(0 0 0 / 0.6)',
                              border: 0,
                              color: '#fff',
                              cursor: 'pointer',
                              zIndex: 2,
                            }}
                          >
                            <X className="w-4 h-4 mx-auto" />
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Media deep-links to the detail page. Class-feed events have no
                              inline preview, so the whole tile is the link; when there IS a
                              cover image + free preview, the hover-to-play button handles it. */}
                          {thumbUrl ? (
                            <Link to={courseHref(product._id)} className="absolute inset-0 flex items-center justify-center">
                              {isLogoImg ? (
                                // fallback studio logo: small + centered on the dark media
                                <img
                                  src={thumbUrl}
                                  alt={product.title}
                                  className="object-contain"
                                  style={{ maxWidth: '48%', maxHeight: '56%' }}
                                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              ) : (
                                // contain, not cover: creators upload posters, slides and
                                // stills at wildly different ratios, and cover was cropping
                                // the title or the subject's head out of the tile.
                                <img
                                  src={thumbUrl}
                                  alt={product.title}
                                  className="w-full h-full object-contain"
                                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              )}
                            </Link>
                          ) : isEvent && ev ? (
                            <Link
                              to={courseHref(product._id)}
                              className="w-full h-full flex flex-col items-center justify-center gap-1.5"
                              style={{ textDecoration: 'none' }}
                            >
                              <div className="mono-label" style={{ color: 'oklch(0.72 0 0)', letterSpacing: '0.12em' }}>
                                {page.displayName}
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.1em', color: 'oklch(0.82 0 0)' }}>
                                  {ev.month}
                                </span>
                                <span className="font-hj-display" style={{ fontSize: 46, fontWeight: 800, lineHeight: 1, color: '#fff' }}>
                                  {ev.day}
                                </span>
                              </div>
                              {product.instructor && (
                                <div style={{ fontSize: 12.5, color: 'oklch(0.78 0 0)' }}>
                                  with {product.instructor}
                                </div>
                              )}
                            </Link>
                          ) : (
                            <Video className="w-10 h-10 absolute" style={{ color: 'oklch(0.5 0 0)' }} />
                          )}
                          {hasPreview && (
                            <button
                              className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                              onClick={() => setPreviewProductId(product._id)}
                              style={{ background: 'oklch(0 0 0 / 0.4)', border: 0, cursor: 'pointer' }}
                            >
                              <div
                                className="inline-flex items-center gap-1.5"
                                style={{
                                  padding: '7px 14px',
                                  borderRadius: 999,
                                  background: 'var(--hj-bg)',
                                  color: 'var(--hj-ink)',
                                  fontSize: 12.5,
                                  fontWeight: 600,
                                }}
                              >
                                <Eye className="w-3.5 h-3.5" /> Free preview
                              </div>
                            </button>
                          )}
                          {isReplay && (
                            <span
                              className="mono-label absolute"
                              style={{
                                top: 10,
                                left: 10,
                                padding: '3px 9px',
                                borderRadius: 999,
                                background: 'var(--hj-yellow)',
                                color: 'oklch(0.18 0.005 70)',
                                fontWeight: 700,
                              }}
                            >
                              Featured event replay
                            </span>
                          )}
                          {!isReplay && product.pricingMode === 'subscription' && (
                            <span
                              className="mono-label absolute"
                              style={{
                                top: 10,
                                left: 10,
                                padding: '3px 9px',
                                borderRadius: 999,
                                background: 'var(--hj-blue)',
                                color: 'oklch(0.18 0.005 70)',
                                fontWeight: 700,
                              }}
                            >
                              Monthly
                            </span>
                          )}
                          {product.productType === 'series' && (
                            <span
                              className="mono-label absolute"
                              style={{
                                top: 10,
                                right: 10,
                                padding: '3px 9px',
                                borderRadius: 999,
                                background: 'oklch(0 0 0 / 0.6)',
                                color: '#fff',
                              }}
                            >
                              {(() => {
                                const counts: Record<string, number> = {};
                                product.videos.forEach(v => {
                                  const t = (v as any).fileType || 'video';
                                  counts[t] = (counts[t] || 0) + 1;
                                });
                                const parts: string[] = [];
                                if (counts.video) parts.push(`${counts.video} vid`);
                                if (counts.pdf) parts.push(`${counts.pdf} PDF`);
                                if (counts.image) parts.push(`${counts.image} img`);
                                if (counts.file) parts.push(`${counts.file} file`);
                                return parts.join(' · ') || `${product.videos.length} items`;
                              })()}
                            </span>
                          )}
                          {product.format === 'series' && product.productType !== 'series' && (
                            <span
                              className="mono-label absolute"
                              style={{
                                top: 10,
                                right: 10,
                                padding: '3px 9px',
                                borderRadius: 999,
                                background: 'oklch(0 0 0 / 0.6)',
                                color: '#fff',
                              }}
                            >
                              {epCount} episode{epCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {product.productType === 'bundle' && (
                            <span
                              className="mono-label absolute"
                              style={{ top: 10, right: 10, padding: '3px 9px', borderRadius: 999, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)', fontWeight: 700 }}
                            >
                              {(product.bundle as import('@/services/creatorApi').BundleCard | undefined)?.mode === 'pass' ? 'Season pass' : `Bundle · ${(product.bundle as import('@/services/creatorApi').BundleCard | undefined)?.itemCount ?? 0}`}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex-1 flex flex-col" style={{ padding: '15px 18px 18px' }}>
                      <Link
                        to={courseHref(product._id)}
                        className="font-hj-display m-0 block line-clamp-2 hover:underline"
                        style={{ fontSize: 18, fontWeight: 800, color: 'var(--hj-beige-ink)', lineHeight: 1.15, letterSpacing: '-0.01em', textDecoration: 'none', minHeight: '2.3em' }}
                      >
                        {product.title}
                      </Link>
                      <CoOwnerLine product={product} storeSlug={page.slug} />

                      {ev && (
                        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--hj-beige-ink-2)' }}>
                          {ev.full} · {ev.time}
                        </div>
                      )}
                      {product.productType === 'bundle' && (() => {
                        const b = product.bundle as import('@/services/creatorApi').BundleCard | undefined;
                        if (!b) return null;
                        const parts = [
                          b.mode === 'pass' && b.through ? `Every event through ${new Date(b.through).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })}` : null,
                          b.saveCents > 0 ? `Save $${(b.saveCents / 100).toFixed(b.saveCents % 100 ? 2 : 0)}` : null,
                        ].filter(Boolean);
                        return parts.length ? (
                          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{parts.join(' · ')}</div>
                        ) : null;
                      })()}

                      {product.instructor ? (
                        <div className="line-clamp-1" style={{ marginTop: 5, fontSize: 13, color: 'var(--hj-beige-ink-2)' }}>
                          {product.instructor}
                        </div>
                      ) : product.description ? (
                        <p className="line-clamp-2 m-0" style={{ marginTop: 5, fontSize: 13, color: 'var(--hj-beige-ink-2)', lineHeight: 1.45 }}>
                          {product.description}
                        </p>
                      ) : null}

                      {(product.tags?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1" style={{ marginTop: 8 }}>
                          {product.tags!.slice(0, 4).map((t) => (
                            <span
                              key={t}
                              className="mono-label"
                              style={{ padding: '2px 7px', borderRadius: 999, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-3)', fontSize: 10 }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}

                      {((summary && summary.totalReviews > 0) || (product.memberCount ?? 0) > 0) && (
                        <div className="flex items-center gap-3" style={{ marginTop: 8 }}>
                          {summary && summary.totalReviews > 0 && (
                            <span className="inline-flex items-center gap-1 tabular" style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)' }}>
                              {/* Card is cream paper — brand yellow stars are unreadable on it. */}
                              <span style={{ color: 'var(--hj-beige-ink)' }}>
                                {'★'.repeat(Math.round(summary.averageRating))}{'☆'.repeat(5 - Math.round(summary.averageRating))}
                              </span>
                              <span>({summary.totalReviews})</span>
                            </span>
                          )}
                          {(product.memberCount ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)' }}>
                              <Users className="w-3.5 h-3.5" />
                              {product.memberCount}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Price + availability — pinned to the bottom so buttons align across cards */}
                      <div className="flex items-end justify-between gap-2 mt-auto" style={{ paddingTop: 14 }}>
                        <div className="flex items-baseline gap-1.5">
                          <span className="tabular" style={{ fontSize: 20, fontWeight: 800, color: 'var(--hj-beige-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                            {hasMemberPrice ? formatPrice(product.memberPriceCents!) : (product.pricingMode === 'subscription' && product.price === 0 ? 'Free' : formatPrice(product.price))}
                            {product.pricingMode === 'subscription' && product.price > 0 && (
                              <span style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)', fontWeight: 400 }}>/mo</span>
                            )}
                          </span>
                          {hasMemberPrice && (
                            <span style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>member</span>
                          )}
                        </div>
                        {isEvent && typeof product.seatsRemaining === 'number' && (
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink-2)' }}>
                            {seatsFull ? 'Full' : 'Seats remaining'}
                          </span>
                        )}
                      </div>
                      {hasMemberPrice && (
                        <div className="tabular" style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                          {formatPrice(product.price)} non-member
                        </div>
                      )}
                      {!isEvent && walletCents > 0 && product.price > 0 && product.pricingMode !== 'subscription' && (
                        <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}>
                          Your {formatPrice(walletCents)} Hey Joe credit applies at checkout.
                        </div>
                      )}

                      {/* Full-width CTA + share */}
                      <div className="flex items-center gap-2" style={{ marginTop: 14 }}>
                        {isEvent ? (
                          <Link
                            to={courseHref(product._id)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5"
                            style={{ padding: '11px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}
                          >
                            View class <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleBuy(product._id)}
                            disabled={checkingOut === product._id}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
                            style={{ padding: '11px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: checkingOut === product._id ? 0.6 : 1 }}
                          >
                            {checkingOut === product._id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Play className="w-3.5 h-3.5" />
                            )}
                            {product.pricingMode === 'subscription' ? 'Subscribe' : 'Buy'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleShareProduct(product._id, product.title)}
                          aria-label="Share"
                          className="inline-flex items-center justify-center flex-shrink-0"
                          style={{ width: 42, height: 42, borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', cursor: 'pointer' }}
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {pastProducts.length > 0 && (
          <div style={{ marginTop: 36 }}>
            <div className="flex items-baseline gap-3" style={{ marginBottom: 14 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 20, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                Past classes
              </h2>
              <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{pastProducts.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pastProducts.map(p => {
                const ev = formatEventDate(p.eventDate);
                const cover = p.coverImage
                  ? (p.coverImage.startsWith('http') || p.coverImage.startsWith('/api/') ? p.coverImage : `/api/s3/${p.coverImage}`)
                  : null;
                return (
                  <div
                    key={p._id}
                    className="flex gap-3"
                    style={{ padding: 12, borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-beige-line)', background: 'var(--hj-surface-1)' }}
                  >
                    {cover && (
                      <img
                        src={cover}
                        alt={p.title}
                        className="flex-shrink-0"
                        style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', filter: 'grayscale(0.6)', opacity: 0.85 }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="mono-label" style={{ padding: '2px 7px', borderRadius: 999, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-2)' }}>
                          In the past
                        </span>
                        {(p.bookedCount ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                            <Users className="w-3 h-3" /> {p.bookedCount} booked
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700, color: 'var(--hj-ink)', lineHeight: 1.25 }}>{p.title}</div>
                      <CoOwnerLine product={p} storeSlug={page.slug} />
                      {ev && (
                        <div className="inline-flex items-center gap-1.5" style={{ marginTop: 4, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
                          <CalendarDays className="w-3.5 h-3.5" /> {ev.full}
                        </div>
                      )}
                      {p.description && (
                        <p className="line-clamp-2 m-0" style={{ marginTop: 6, fontSize: 13, color: 'var(--hj-ink-3)', lineHeight: 1.45 }}>
                          {p.description.replace(/<[^>]+>/g, ' ')}
                        </p>
                      )}
                      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: 'var(--hj-ink-4)' }}>Not available</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center" style={{ padding: '40px 0 12px' }}>
          <p className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>
            Powered by{' '}
            <a
              href="https://plus.heyjoe.io"
              style={{ color: 'var(--hj-ink-3)', textDecoration: 'underline' }}
            >
              Hey Joe Plus
            </a>
          </p>
        </div>
      </section>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSuccess={() => { setShowLogin(false); if (afterLogin === 'follow') toggleFollow(true); else setShowMessage(true); }}
      />

      <Dialog open={showMessage} onOpenChange={(o) => { if (!sending) setShowMessage(o); }}>
        <DialogContent className="sm:max-w-[520px]" style={{ width: 'calc(100vw - 32px)' }}>
          <DialogHeader>
            <DialogTitle>Message {firstName}</DialogTitle>
          </DialogHeader>
          <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
            Your note goes to {firstName}'s inbox. They reply to you by email.
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            placeholder="What are you hoping to work on?"
            rows={6}
            autoFocus
            style={{
              width: '100%',
              marginTop: 4,
              padding: '12px 14px',
              borderRadius: 'var(--hj-r-md)',
              border: '1px solid var(--hj-hairline)',
              background: 'var(--hj-surface-1)',
              color: 'var(--hj-ink)',
              fontSize: 15,
              lineHeight: 1.5,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div className="flex items-center justify-between gap-3" style={{ marginTop: 4 }}>
            <span className="mono-label tabular" style={{ color: 'var(--hj-ink-4)' }}>{note.trim().length} / 2000</span>
            <button
              type="button"
              onClick={handleSendNote}
              disabled={sending || note.trim().length < 10}
              className="inline-flex items-center gap-2"
              style={{ ...primaryPill, minHeight: 44, fontSize: 14, opacity: sending || note.trim().length < 10 ? 0.6 : 1 }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              {sending ? 'Sending' : 'Send'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default CreatorStorePage;
